import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Upload, Plus, Trash2 } from "lucide-react";
import { QuestionModal } from "./QuestionModal";
import { subModuleService } from "../../services/subModuleService";
import { SubModule } from "types/subModule";
import { useToast } from "../ui/use-toast";
import { questionService } from "../../services/questionService";
import { moduleService } from "../../services/moduleService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Spinner } from "../ui/Spinner";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";

interface ReelItem {
  id: string;
  moduleId: number;
  name: string;
  type: string;
  content: string;
  videoUrl: string;
  sequenceNumber: number;
  pointsAwarded: number;
  startDate?: string;
  endDate?: string;
  questions: any[];
}

interface ReelsModuleProps {
  moduleId?: string;
  onSave: (data: ReelItem[]) => void;
}

function getFileTypeFromPath(path: string): 'pdf' | 'image' | 'audio' | undefined {
  if (!path) return undefined;
  const ext = path.split('.').pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "gif"].includes(ext || "")) return "image";
  if (["mp3", "wav", "ogg"].includes(ext || "")) return "audio";
  if (ext === "pdf") return "pdf";
  return undefined;
}

// QuestionsView modal (read-only)
function QuestionsView({ isOpen, onClose, questions }: { isOpen: boolean; onClose: () => void; questions: any[] }) {
  const [previewImg, setPreviewImg] = React.useState<string | null>(null);
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Questions (View Only)</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto flex-1 pr-2">
            {questions.length === 0 && <div className="text-gray-500">No questions available.</div>}
            {questions.map((q, qIndex) => (
              <div key={q.id || qIndex} className="space-y-2 p-4 border rounded-lg">
                <div className="font-semibold">Q{qIndex + 1}: {q.question || q.text || q.questionText}</div>
                <div className="ml-4 space-y-1">
                  {Array.isArray(q.options) && q.options.map((opt: string, i: number) => (
                    <div key={i} className={q.correctAnswer === opt || q.correctAnswer === i ? "font-bold text-green-700" : ""}>
                      {String.fromCharCode(65 + i)}. {opt}
                      {q.correctAnswer === opt || q.correctAnswer === i ? " (Correct)" : ""}
                    </div>
                  ))}
                </div>
                {/* Preview for filePath */}
                {q.filePath && (
                  <div className="mt-2">
                    <div className="font-medium">Attachment Preview:</div>
                    <div className="flex items-center gap-2">
                      {getFileTypeFromPath(q.filePath) === 'image' ? (
                        <img
                          src={q.filePath}
                          alt="Preview"
                          className="h-10 w-10 object-cover rounded cursor-pointer"
                          onClick={() => setPreviewImg(q.filePath)}
                        />
                      ) : getFileTypeFromPath(q.filePath) === 'pdf' ? (
                        <a href={q.filePath} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">PDF</a>
                      ) : getFileTypeFromPath(q.filePath) === 'audio' ? (
                        <audio controls src={q.filePath} className="h-8" />
                      ) : null}
                      <span className="text-xs">{q.filePath.split("/").pop()}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Image Preview Modal */}
      <Dialog open={!!previewImg} onOpenChange={() => setPreviewImg(null)}>
        <DialogContent className="flex items-center justify-center">
          {previewImg && (
            <img src={previewImg} alt="Full Preview" className="max-h-[80vh] max-w-full rounded shadow" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReelsModule({ moduleId, onSave }: ReelsModuleProps) {
  const { toast } = useToast();
  const [reelItems, setReelItems] = useState<ReelItem[]>([]);
  const [selectedReelId, setSelectedReelId] = useState<string | null>(null);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalQuestions, setModalQuestions] = useState<any[]>([]);
  const [isQuestionsViewOpen, setIsQuestionsViewOpen] = useState(false);
  const [viewQuestions, setViewQuestions] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [reelToDelete, setReelToDelete] = useState<string | null>(null);
  const [reelPendingVideoRemove, setReelPendingVideoRemove] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const fetchReels = async () => {
      if (moduleId) {
        try {
          setLoading(true);
          const response = await subModuleService.getSubModulesByModuleId(parseInt(moduleId), 'reels');
          // Handle both single object and array responses
          const reelsArray = Array.isArray(response) ? response : [response];
          const reels = reelsArray.map((reel: SubModule) => ({
            id: reel.id.toString(),
            moduleId: reel.moduleId,
            name: reel.name,
            type: reel.type,
            content: reel.content,
            videoUrl: reel.videoUrl,
            sequenceNumber: reel.sequenceNumber,
            pointsAwarded: reel.pointsAwarded,
            startDate: reel.startDate,
            endDate: reel.endDate,
            questions: []
          }));
          setReelItems(reels);
        } catch (error) {
          console.error('Error fetching reels:', error);
          toast({
            title: "Error",
            description: "Failed to fetch reels",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchReels();
  }, [moduleId, toast]);

  const addNewReel = () => {
    const maxSequence = reelItems.reduce((max, reel) => reel.sequenceNumber > max ? reel.sequenceNumber : max, 0);
    const newReel: ReelItem = {
      id: "0",
      moduleId: parseInt(moduleId || "0"),
      name: "",
      type: "reels",
      content: "",
      videoUrl: "",
      sequenceNumber: maxSequence + 1,
      pointsAwarded: 0,
      questions: []
    };
    setReelItems([...reelItems, newReel]);
  };

  const removeReel = async (id: string) => {
    // If the reel hasn't been saved yet (id is "0"), just remove from UI
    if (id === "0") {
      setReelItems(reelItems.filter(item => item.id !== id));
      setIsDeleteDialogOpen(false);
      setReelToDelete(null);
      return;
    }

    try {
      setLoading(true);
      await subModuleService.deleteSubModule(parseInt(id));
      setReelItems(reelItems.filter(item => item.id !== id));
      toast({
        title: "Success",
        description: "Reel deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting reel:', error);
      toast({
        title: "Error",
        description: "Failed to delete reel",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsDeleteDialogOpen(false);
      setReelToDelete(null);
    }
  };

  const handleDeleteClick = (id: string) => {
    setReelToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const updateReelPoints = (id: string, points: string) => {
    setReelItems(reelItems.map(item => 
      item.id === id ? { ...item, pointsAwarded: parseInt(points) || 0 } : item
    ));
  };

  const handleSaveReel = async (reelId: string) => {
    const reel = reelItems.find(item => item.id === reelId);
    if (!reel || !moduleId) return;

    try {
      setLoading(true);
      const subModuleData: Partial<SubModule> = {
        id: isNaN(Number(reel.id)) ? 0 : Number(reel.id),
        moduleId: parseInt(moduleId),
        name: reel.name,
        type: 'reels',
        content: reel.content,
        videoUrl: reel.videoUrl,
        sequenceNumber: reel.sequenceNumber,
        pointsAwarded: reel.pointsAwarded,
        startDate: reel.startDate,
        endDate: reel.endDate
      };

      const savedReel = await subModuleService.saveSubModule(subModuleData);
      
      // Update the reel with the saved data
      setReelItems(reelItems.map(item => 
        item.id === reelId 
          ? { ...item, id: savedReel.id.toString() }
          : item
      ));

      toast({
        title: "Success",
        description: "Reel saved successfully",
      });
    } catch (error) {
      console.error('Error saving reel:', error);
      toast({
        title: "Error",
        description: "Failed to save reel",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSave = (question: any) => {
    if (selectedReelId) {
      setReelItems(reelItems.map(item => 
        item.id === selectedReelId 
          ? { ...item, questions: [...item.questions, question] }
          : item
      ));
    }
  };

  const handleVideoUpload = async (reelId: string, file: File) => {
    try {
      setIsUploading(prev => ({ ...prev, [reelId]: true }));
      setUploadProgress(prev => ({ ...prev, [reelId]: 0 }));
      
      const data = await moduleService.uploadFile(file, (progress) => {
        setUploadProgress(prev => ({ ...prev, [reelId]: progress }));
      });
      
      const videoUrl = data.url;
      setReelItems(reelItems.map(item =>
        item.id === reelId ? { ...item, videoUrl } : item
      ));
      
      toast({
        title: "Success",
        description: "Video uploaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload video",
        variant: "destructive",
      });
    } finally {
      setIsUploading(prev => ({ ...prev, [reelId]: false }));
      setUploadProgress(prev => ({ ...prev, [reelId]: 0 }));
    }
  };

  if (loading) {
    return <Spinner label="Loading..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Reels Configuration</h3>
        <Button onClick={addNewReel}>
          <Plus className="mr-2 h-4 w-4" />
          Add New Reel
        </Button>
      </div>

      {reelItems.map((reel, index) => (
        <Card key={reel.id}>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Reel {index + 1}</CardTitle>
              <div className="flex items-center gap-2">
                <Label htmlFor={`sequence-${reel.id}`} className="shrink-0">Sequence No.</Label>
                <Input
                  id={`sequence-${reel.id}`}
                  type="number"
                  value={reel.sequenceNumber}
                  onChange={(e) => {
                    const newSequence = parseInt(e.target.value, 10);
                    setReelItems(reelItems.map(item =>
                      item.id === reel.id ? { ...item, sequenceNumber: isNaN(newSequence) ? 0 : newSequence } : item
                    ));
                  }}
                  className="w-24"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`name-${reel.id}`}>Name</Label>
                <Input
                  id={`name-${reel.id}`}
                  value={reel.name}
                  onChange={(e) => setReelItems(reelItems.map(item =>
                    item.id === reel.id ? { ...item, name: e.target.value } : item
                  ))}
                  placeholder="Enter reel name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`content-${reel.id}`}>Description</Label>
                <Input
                  id={`content-${reel.id}`}
                  value={reel.content}
                  onChange={(e) => setReelItems(reelItems.map(item =>
                    item.id === reel.id ? { ...item, content: e.target.value } : item
                  ))}
                  placeholder="Enter reel description"
                />
              </div>
              </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col space-y-2">
                <Label htmlFor={`points-${reel.id}`}>Points</Label>
                <Input 
                  id={`points-${reel.id}`} 
                  type="number" 
                  placeholder="Enter points"
                  value={reel.pointsAwarded.toString()}
                  onChange={(e) => updateReelPoints(reel.id, e.target.value)}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <Label htmlFor={`start-date-${reel.id}`}>Start Date</Label>
                <DatePicker
                  id={`start-date-${reel.id}`}
                  selected={reel.startDate ? parseISO(reel.startDate) : null}
                  onChange={(date: Date | null) =>
                    setReelItems(reelItems.map(item =>
                      item.id === reel.id ? { ...item, startDate: date ? format(date, "yyyy-MM-dd") : "" } : item
                    ))
                  }
                  dateFormat="yyyy-MM-dd"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholderText="Select start date"
                  disabled={loading}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <Label htmlFor={`end-date-${reel.id}`}>End Date</Label>
                <DatePicker
                  id={`end-date-${reel.id}`}
                  selected={reel.endDate ? parseISO(reel.endDate) : null}
                  onChange={(date: Date | null) =>
                    setReelItems(reelItems.map(item =>
                      item.id === reel.id ? { ...item, endDate: date ? format(date, "yyyy-MM-dd") : "" } : item
                    ))
                  }
                  dateFormat="yyyy-MM-dd"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholderText="Select end date"
                  minDate={reel.startDate ? parseISO(reel.startDate) : undefined}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`video-url-${reel.id}`}>Video URL</Label>
              <Input
                id={`video-url-${reel.id}`}
                type="url"
                placeholder="Paste video URL here (e.g. YouTube, Vimeo, etc.)"
                value={reel.videoUrl && reel.videoUrl.startsWith('http') ? reel.videoUrl : ''}
                onChange={e => {
                  const url = e.target.value;
                  setReelItems(reelItems.map(item =>
                    item.id === reel.id ? { ...item, videoUrl: url } : item
                  ));
                }}
                disabled={!!(reel.videoUrl && !reel.videoUrl.startsWith('http') && reel.videoUrl !== '')}
              />
              {/* Hidden field for uploaded video URLs */}
              <Input
                type="hidden"
                value={reel.videoUrl && !reel.videoUrl.startsWith('http') ? reel.videoUrl : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>Upload Video</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6"
                onDragOver={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files && e.dataTransfer.files[0];
                  if (file) handleVideoUpload(reel.id, file);
                }}
              >
                <div className="flex flex-row items-center gap-6">
                  <div className="flex flex-col items-center justify-center flex-1">
                    <Upload className="h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2 text-center">Drop your video here or click to browse</p>
                    <input
                      type="file"
                      accept="video/*"
                      style={{ display: "none" }}
                      id={`video-upload-${reel.id}`}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleVideoUpload(reel.id, file);
                      }}
                      disabled={!!(reel.videoUrl && reel.videoUrl.startsWith('http')) || isUploading[reel.id]}
                    />
                    <Button
                      onClick={() => document.getElementById(`video-upload-${reel.id}`)?.click()}
                      disabled={!!(reel.videoUrl && reel.videoUrl.startsWith('http')) || isUploading[reel.id]}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {isUploading[reel.id] ? 'Uploading...' : 'Upload Video'}
                    </Button>
                    
                    {/* Progress Bar */}
                    {isUploading[reel.id] && (
                      <div className="w-full mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress[reel.id] || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress[reel.id] || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                  {reel.videoUrl && !reel.videoUrl.startsWith('http') && (
                    <div className="flex-1 flex items-center justify-center">
                      <video src={reel.videoUrl} controls width="220" className="rounded shadow" />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 p-2"
                        onClick={() => setReelPendingVideoRemove(reel.id)}
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  )}
                  {reel.videoUrl && reel.videoUrl.startsWith('http') && (
                    <div className="flex-1 flex items-center justify-center gap-4">
                      {/\.(mp4|webm|ogg)$/i.test(reel.videoUrl)
                        ? <video src={reel.videoUrl} controls width="220" className="rounded shadow" />
                        : <a href={reel.videoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Video URL</a>
                      }
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 p-2"
                        onClick={() => setReelPendingVideoRemove(reel.id)}
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-end gap-2">
                <Button
                  variant="default"
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white"
                  onClick={() => handleSaveReel(reel.id)}
                  disabled={loading}
                >
                  Save Reel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white"
                  onClick={async () => {
                    // Only open QuestionModal if reel is saved (id is not '0' and is a number)
                    if (reel.id && reel.id !== '0' && !isNaN(Number(reel.id))) {
                      setSelectedReelId(reel.id);
                      setLoading(true);
                      try {
                        const questions = await questionService.getQuestions(Number(reel.id));
                        setModalQuestions(questions);
                        setIsQuestionModalOpen(true);
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: "Failed to fetch questions",
                          variant: "destructive",
                        });
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      toast({
                        title: "Save the reel first",
                        description: "Please save the reel before adding questions.",
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={loading}
                >
                  Add Questions
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const questions = await questionService.getQuestions(Number(reel.id));
                      setViewQuestions(questions);
                      setIsQuestionsViewOpen(true);
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "Failed to fetch questions",
                        variant: "destructive",
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  View Questions
                </Button>
              </div>
              {reel.questions.length > 0 && (
                <div className="mt-2 space-y-2">
                  {reel.questions.map((question, qIndex) => (
                    <div key={qIndex} className="p-2 bg-gray-50 rounded">
                      {question.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* QuestionsView Modal */}
      <QuestionsView
        isOpen={isQuestionsViewOpen}
        onClose={() => setIsQuestionsViewOpen(false)}
        questions={viewQuestions}
      />

      <QuestionModal 
        reelId={selectedReelId || ""}
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false);
          setSelectedReelId(null);
        }}
        onSave={handleQuestionSave}
        initialQuestions={Array.isArray(modalQuestions) ? modalQuestions : []}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Reel</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this reel? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setReelToDelete(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => reelToDelete && removeReel(reelToDelete)}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Remove Confirmation Dialog */}
      <Dialog open={!!reelPendingVideoRemove} onOpenChange={() => setReelPendingVideoRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Video</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this video? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReelPendingVideoRemove(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => {
              setReelItems(reelItems.map(item =>
                item.id === reelPendingVideoRemove ? { ...item, videoUrl: '' } : item
              ));
              setReelPendingVideoRemove(null);
            }}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}